import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface HeaderData {
  companyId: string | null;
  companyLogo: string | null;
  companyName: string | null;
  profilePicture: string | null;
  isAdmin: boolean;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  onboardingComplete: boolean;
}

export const headerDataQueryKey = (userId: string | undefined) => ['headerData', userId];

export const headerDataQueryFn = async (userId: string): Promise<HeaderData> => {
  // Step 1: Fetch user profile (includes onboarding fields)
  const { data: userData } = await supabase
    .from('users')
    .select('profile_picture_url, company_id, first_name, last_name, phone')
    .eq('id', userId)
    .maybeSingle();

  const profilePicture = userData?.profile_picture_url || null;
  const companyId = userData?.company_id || null;
  const firstName = userData?.first_name || null;
  const lastName = userData?.last_name || null;
  const phone = userData?.phone || null;

  const onboardingComplete = !!(firstName && lastName && companyId);

  // Step 2: Fetch company info AND admin role in parallel
  const [companyResult, roleResult] = await Promise.all([
    companyId
      ? supabase
          .from('companies')
          .select('logo_url, name')
          .eq('id', companyId)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle(),
  ]);

  return {
    companyId,
    companyLogo: companyResult.data?.logo_url || null,
    companyName: companyResult.data?.name || null,
    profilePicture,
    isAdmin: !!roleResult.data,
    firstName,
    lastName,
    phone,
    onboardingComplete,
  };
};

export const useHeaderData = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: headerDataQueryKey(user?.id),
    queryFn: async (): Promise<HeaderData> => {
      if (!user) {
        return {
          companyId: null,
          companyLogo: null,
          companyName: null,
          profilePicture: null,
          isAdmin: false,
          firstName: null,
          lastName: null,
          phone: null,
          onboardingComplete: false,
        };
      }
      return headerDataQueryFn(user.id);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
